import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";

/**
 * DepartmentPicker — select-or-create department inline.
 *
 * Props:
 *  - value: department_id (string)
 *  - onChange: (id: string) => void
 *  - required: bool
 *  - testid: string
 */
export default function DepartmentPicker({ value, onChange, required = false, testid = "department-picker" }) {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#3b82f6");

  useEffect(() => {
    api.get("/departments").then(({ data }) => setItems(data));
  }, []);

  async function createInline() {
    if (!newName.trim()) return;
    try {
      const { data } = await api.post("/departments", { name: newName.trim(), color: newColor });
      setItems((prev) => {
        if (prev.find((x) => x.id === data.id)) return prev;
        return [...prev, data].sort((a, b) => a.name.localeCompare(b.name));
      });
      onChange(data.id);
      setShowNew(false);
      setNewName(""); setNewColor("#3b82f6");
      toast.success(`Abteilung "${data.name}" angelegt`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  }

  return (
    <div>
      <div className="flex gap-2">
        <select
          value={value || ""}
          onChange={(e) => {
            if (e.target.value === "__new__") setShowNew(true);
            else onChange(e.target.value);
          }}
          data-testid={testid}
          className={`flex-1 px-3 py-2 bg-zinc-950 border rounded text-sm text-zinc-100 ${required && !value ? "border-amber-500/60 ring-1 ring-amber-500/30" : "border-zinc-800"}`}
        >
          <option value="">— keine Abteilung —</option>
          {items.map((d) => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
          <option value="__new__">➕ Neue Abteilung …</option>
        </select>
      </div>
      {showNew && (
        <div className="mt-2 p-3 bg-blue-950/30 border border-blue-900/40 rounded flex items-end gap-2">
          <div className="flex-1">
            <label className="text-[10px] uppercase text-blue-300">Neue Abteilung</label>
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") createInline(); }}
              placeholder='z.B. "Amazon Nord" oder "Sondertransporte"'
              data-testid="department-new-name"
              className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"
            />
          </div>
          <input type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)} className="w-10 h-10 bg-zinc-900 border border-zinc-800 rounded cursor-pointer" title="Farbe"/>
          <button onClick={createInline} data-testid="department-new-save" className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5"/>Anlegen</button>
          <button onClick={() => { setShowNew(false); setNewName(""); }} className="p-2 text-zinc-400"><X className="w-4 h-4"/></button>
        </div>
      )}
    </div>
  );
}

/** DepartmentBadge — colored badge for lists */
export function DepartmentBadge({ id, items = null, testid }) {
  const [allDepts, setAllDepts] = useState(items);
  useEffect(() => {
    if (!items) api.get("/departments").then(({ data }) => setAllDepts(data));
  }, [items]);
  if (!id) return null;
  const dept = (allDepts || []).find((d) => d.id === id);
  if (!dept) return null;
  return (
    <span
      data-testid={testid || `dept-badge-${id}`}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium"
      style={{ backgroundColor: `${dept.color}22`, color: dept.color, border: `1px solid ${dept.color}55` }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: dept.color }}/>
      {dept.name}
    </span>
  );
}
